import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { selectEmailMetadataForEmail, selectShouldPollMetadata } from './emailMetadata'
import { EmailResponseResult } from '../../models/EmailResponse'
import { Citation } from '../../views/AIAgent/AIAgentResultCitations/AIAgentResultCitations'
import { hasTenMinutesPassed } from '../utils'

interface EmailResponseState {
  isLoading: boolean
  pollingEnabled: boolean
  emailResponse: EmailResponseResult | null
  editingEmailResponse: boolean
  interactionIsLoading: boolean
  editingIsLoading: boolean
  executeIsLoading: boolean
  aiAgentId: string | null
  citations: Citation[]
  citationIsLoading: boolean
  emailTypeChanged: boolean
}

const initialState: EmailResponseState = {
  isLoading: true,
  pollingEnabled: false,
  emailResponse: null,
  editingEmailResponse: false,
  interactionIsLoading: false,
  editingIsLoading: false,
  executeIsLoading: false,
  aiAgentId: null,
  citations: [],
  citationIsLoading: false,
  emailTypeChanged: false,
}

export const emailResponseSlice = createSlice({
  name: 'emailResponse',
  initialState,
  reducers: {
    setEmailResponseLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    startEmailResponsePolling: (state) => {
      state.pollingEnabled = true
    },
    stopEmailResponsePolling: (state) => {
      state.pollingEnabled = false
    },
    setEmailResponse: (state, action: PayloadAction<EmailResponseResult>) => {
      state.emailResponse = { ...state.emailResponse, ...action.payload }
    },
    clearEmailResponse: (state) => {
      state.emailResponse = null
    },
    setEditingEmailResponse: (state, action: PayloadAction<boolean>) => {
      state.editingEmailResponse = action.payload
    },
    setEmailResponseInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.interactionIsLoading = action.payload
    },
    setEmailResponseEditingIsLoading: (state, action: PayloadAction<boolean>) => {
      state.editingIsLoading = action.payload
    },
    setEmailResponseExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executeIsLoading = action.payload
    },
    setEmailResponseAiAgentId: (state, action: PayloadAction<string>) => {
      state.aiAgentId = action.payload
    },
    setEmailResponseCitations: (state, action: PayloadAction<Citation[]>) => {
      state.citations = action.payload
    },
    setEmailResponseCitationLoading: (state, action: PayloadAction<boolean>) => {
      state.citationIsLoading = action.payload
    },
    setEmailResponseEmailTypeChanged: (state, action: PayloadAction<boolean>) => {
      state.emailTypeChanged = action.payload
    },
    resetEmailResponse: () => initialState,
  },
})

export const {
  setEmailResponseLoading,
  startEmailResponsePolling,
  stopEmailResponsePolling,
  setEmailResponse,
  clearEmailResponse,
  setEditingEmailResponse,
  setEmailResponseInteractionLoading,
  setEmailResponseEditingIsLoading,
  setEmailResponseExecutionLoading,
  setEmailResponseAiAgentId,
  setEmailResponseCitations,
  setEmailResponseCitationLoading,
  setEmailResponseEmailTypeChanged,
  resetEmailResponse,
} = emailResponseSlice.actions

export const selectEmailResponseLoading = (state: RootState) => state.emailHub.emailResponse.isLoading

export const selectEmailResponsePollingEnabled = (state: RootState) => state.emailHub.emailResponse.pollingEnabled

export const selectEmailResponse = (state: RootState) => state.emailHub.emailResponse.emailResponse

export const selectShouldPollEmailResponse = createSelector(
  [
    (state: RootState, emailId: string) => selectShouldPollMetadata(state, emailId),
    (state: RootState, emailId: string) => selectEmailMetadataForEmail(state, emailId),
    selectEmailResponse,
  ],
  (shouldPollMetadata, emailMetadata, emailResponse) => {
    if (shouldPollMetadata || emailResponse) {
      return false
    }

    if (!emailMetadata) {
      return false
    }

    const { responseProcessing } = emailMetadata

    return responseProcessing.finishedAt && !hasTenMinutesPassed(responseProcessing.finishedAt)
  },
)

export const selectEmailResponseEditing = (state: RootState) => state.emailHub.emailResponse.editingEmailResponse

export const selectEmailResponseInteractionLoading = (state: RootState) => state.emailHub.emailResponse.interactionIsLoading

export const selectEmailResponseEditingLoading = (state: RootState) => state.emailHub.emailResponse.editingIsLoading

export const selectEmailResponseExecutionLoading = (state: RootState) => state.emailHub.emailResponse.executeIsLoading

export const selectEmailResponseAiAgentId = (state: RootState) => state.emailHub.emailResponse.aiAgentId

export const selectEmailResponseCitations = (state: RootState) => state.emailHub.emailResponse.citations

export const selectEmailResponseCitationIsLoading = (state: RootState) => state.emailHub.emailResponse.citationIsLoading

export const selectEmailResponseEmailTypeChanged = (state: RootState) => state.emailHub.emailResponse.emailTypeChanged
